/** @jsx jsx */
import { Grid, Spacer, Spinner } from "@cd2/components/build/es6"
import { P } from "@cd2/text/build/es6"
import { jsx } from "@emotion/core"
import React, { useState } from "react"
import { ConfirmCancelButtons } from "../interfaceParts/ConfirmCancelButtons"
import { TextGroupMedium } from "../interfaceParts/Title"
import { AllUserSelect } from "../pages/user/UserResultsShowPage/UserSelectForFlowFileApproval"
import { FlowResultFileApprovalStatuses } from "../pages/user/UserResultsShowPage/FlowResultFileApprovalStatuses"
import { IRecord } from "../types/schema"
import { anonymiseEmail } from "../utils/email"
import { pluralize } from "../utils/string"
import { useEvents } from "../utils/useEvents"
import { useAsyncCallback } from "./useAsyncCallback"
import { useAsyncEffect } from "./useAsyncEffect"
import { useDialogueConfirmation2 } from "./useDialogConfirmation"

const pendingFileApprovalFilter = {
  or: [
    { matches: { approvalStatus: FlowResultFileApprovalStatuses.Pending } },
    { matches: { approvalStatus: FlowResultFileApprovalStatuses.RequestedChanges } },
  ],
}

type ArchiveNeeds = {
  pendingCasesCount: number
  filesNeedingForwardCount: number
}

export function useArchiveUserDialog() {
  const events = useEvents()

  return useDialogueConfirmation2<boolean, { users: string[] }>({
    Component({ dialogData, onResolve, registerOutsideClick }) {
      const [needs, setNeeds] = useState<ArchiveNeeds | null>(null)
      const [forwardFilesToId, setForwardFilesToId] = useState<string | null>(null)
      const [forwardCasesToId, setForwardCasesToId] = useState<string | null>(null)
      const [error, setError] = useState<string | null>(null)
      const userCount = dialogData.users.length
      const excludeUsersFilter = {
        and: [
          { not: { matches: { id: { or: dialogData.users } } } },
          { matches: { isAdmin: false } },
        ],
      }

      useAsyncEffect(async () => {
        const [pendingCasesCount, resultFiles] = await Promise.all([
          window.skeem.fetch2<number>({
            count: {
              name: "flowResults",
              matches: {
                user: { or: dialogData.users },
                status: "pending",
                completedDate: null,
              },
            },
          }),
          window.skeem.fetch2<IRecord<"flowResultFiles", "supportUsers">[]>({
            model: {
              name: "flowResultFiles",
              attributes: ["supportUsers"],
              filter: {
                and: [
                  { matches: { supportUsers: { some: { id: { or: dialogData.users } } } } },
                  pendingFileApprovalFilter,
                ],
              },
            },
          }),
        ])

        const filesNeedingForwardCount = resultFiles.filter(file =>
          file.supportUsers.every(su => dialogData.users.includes(su.id)),
        ).length

        setNeeds({ pendingCasesCount, filesNeedingForwardCount })
      }, [dialogData.users.join(",")])

      const handleArchive = useAsyncCallback(async () => {
        if (!needs) return
        if (needs.filesNeedingForwardCount > 0 && !forwardFilesToId) return
        if (needs.pendingCasesCount > 0 && !forwardCasesToId) return

        setError(null)
        try {
          await archiveUsers(events, dialogData.users, {
            forwardFilesToId,
            forwardCasesToId,
          })
          onResolve(true)
        } catch (e) {
          console.error(e)
          setError(e instanceof Error ? e.message : "Failed to archive users")
        }
      })

      const isLoading = handleArchive.running || needs === null
      const canConfirm =
        needs !== null &&
        (needs.filesNeedingForwardCount === 0 || Boolean(forwardFilesToId)) &&
        (needs.pendingCasesCount === 0 || Boolean(forwardCasesToId))

      registerOutsideClick(() => {
        if (!handleArchive.running) onResolve(false)
      })

      return (
        <div>
          <TextGroupMedium
            heading={userCount === 1 ? "Archive user" : "Archive users"}
            body={`Are you sure you wish to archive ${pluralize(userCount, "%c user%s")}?`}
          />
          {needs === null ? (
            <React.Fragment>
              <Spacer size={16} />
              <Spinner color="primary" size={24} />
            </React.Fragment>
          ) : (
            <React.Fragment>
              {needs.filesNeedingForwardCount > 0 && (
                <React.Fragment>
                  <Spacer size={16} />
                  <P>
                    {pluralize(
                      needs.filesNeedingForwardCount,
                      "%c pending file request%s %[needs,need] forwarding",
                    )}
                  </P>
                  <Spacer size={8} />
                  <AllUserSelect
                    label="Forward pending file requests to"
                    value={forwardFilesToId}
                    onChange={user => setForwardFilesToId(user.id)}
                    skeemFilter={excludeUsersFilter}
                    disabled={handleArchive.running}
                  />
                </React.Fragment>
              )}
              {needs.pendingCasesCount > 0 && (
                <React.Fragment>
                  <Spacer size={16} />
                  <P>
                    {pluralize(needs.pendingCasesCount, "%c open case%s %[needs,need] forwarding")}
                  </P>
                  <Spacer size={8} />
                  <AllUserSelect
                    label="Forward open cases to"
                    value={forwardCasesToId}
                    onChange={user => setForwardCasesToId(user.id)}
                    skeemFilter={excludeUsersFilter}
                    disabled={handleArchive.running}
                  />
                </React.Fragment>
              )}
            </React.Fragment>
          )}
          {error && (
            <React.Fragment>
              <Spacer size={16} />
              <P color="red">{error}</P>
            </React.Fragment>
          )}
          <Spacer size={16} />
          {handleArchive.running ? (
            <Grid columns={["max-content", "auto"]} gap={12} alignItems="center">
              <Spinner size={24} color="primary" />
              <P blockSpacing={0}>Archiving users...</P>
            </Grid>
          ) : (
            <ConfirmCancelButtons
              onConfirm={handleArchive}
              onCancel={() => onResolve(false)}
              buttonOpts={{
                confirm: {
                  children: "Yes, archive",
                  disabled: isLoading || !canConfirm,
                },
                cancel: { children: "Cancel" },
              }}
            />
          )}
        </div>
      )
    },
  })
}

async function archiveUsers(
  events: ReturnType<typeof useEvents>,
  userIds: string[],
  opts: { forwardFilesToId: string | null; forwardCasesToId: string | null },
) {
  const users = await window.skeem.fetch2<IRecord<"skeem_users", "employee" | "skeem_email">[]>({
    model: {
      name: "skeem_users",
      attributes: ["skeem_email", "employee"],
      filter: { matches: { id: { or: userIds }, archived: false } },
    },
  })

  const foundUserIds = users.map(u => u.id)
  if (foundUserIds.length === 0) return

  const resultFiles = await window.skeem.fetch2<IRecord<"flowResultFiles", "supportUsers">[]>({
    model: {
      name: "flowResultFiles",
      attributes: ["supportUsers"],
      filter: {
        and: [
          { matches: { supportUsers: { some: { id: { or: foundUserIds } } } } },
          pendingFileApprovalFilter,
        ],
      },
    },
  })

  const fileUpdates = resultFiles
    .map(file => {
      const currentSupportIds = file.supportUsers.map(u => u.id)
      const leaversInFile = currentSupportIds.filter(id => foundUserIds.includes(id))
      const remainingSupportIds = currentSupportIds.filter(id => !foundUserIds.includes(id))
      if (leaversInFile.length === 0) return null

      const updates: ({ remove: string } | { add: string })[] = leaversInFile.map(id => ({
        remove: id,
      }))

      if (remainingSupportIds.length === 0 && opts.forwardFilesToId) {
        updates.push({ add: opts.forwardFilesToId })
      }

      return {
        update: {
          id: file.id,
          supportUsers: updates,
          forwardNotes:
            remainingSupportIds.length === 0
              ? "This file request has been automatically forwarded."
              : undefined,
        },
      }
    })
    .filter(Boolean)

  if (fileUpdates.length > 0) {
    await window.skeem.mutate({ flowResultFiles: fileUpdates })
  }

  if (opts.forwardCasesToId) {
    const cases = await window.skeem.fetch2<IRecord<"flowResults">[]>({
      model: {
        name: "flowResults",
        attributes: [],
        filter: {
          matches: { user: { or: foundUserIds }, status: "pending", completedDate: null },
        },
      },
    })
    if (cases.length > 0) {
      await window.skeem.mutate({
        flowResults: cases.map(c => ({
          update: { id: c.id, user: { set: opts.forwardCasesToId } },
        })),
      })
    }
  }

  const managedTeams = await window.skeem.fetch2<IRecord<"teams", "leaders" | "leadersToNotify">[]>(
    {
      model: {
        name: "teams",
        attributes: ["leaders", "leadersToNotify"],
        filter: {
          exists: {
            path: [{ name: "leaders", filter: { matches: { id: { or: foundUserIds } } } }],
          },
        },
      },
    },
  )

  const teamUpdates = managedTeams
    .map(team => {
      const leaversInTeam = team.leaders.filter(l => foundUserIds.includes(l.id)).map(l => l.id)
      if (leaversInTeam.length === 0) return null

      const leaversInNotify = team.leadersToNotify
        .filter(l => foundUserIds.includes(l.id))
        .map(l => l.id)

      return {
        update: {
          id: team.id,
          leaders: leaversInTeam.map(id => ({ remove: id })),
          leadersToNotify:
            leaversInNotify.length > 0 ? leaversInNotify.map(id => ({ remove: id })) : undefined,
        },
      }
    })
    .filter(Boolean)

  if (teamUpdates.length > 0) {
    await window.skeem.mutate({ teams: teamUpdates })
  }

  await window.skeem.mutate({
    skeem_users: users.map(user => ({
      update: {
        id: user.id,
        isLeaver: true,
        archived: true,
        employee: user.employee ? { update: { id: user.employee.id, archived: true } } : undefined,
        skeem_email: anonymiseEmail(user.skeem_email, "leaver"),
        notifiedForSkippedFiles: { set: null },
      },
    })),
  })

  await Promise.all(foundUserIds.map(id => events.message("skeem_users", id, "Marked as a leaver")))
}
